import {
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Switch,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import { useAdminBillingDocumentController_findOneById } from '@api-client/generated/AdminBillingDocumentController/findOneById';
import { useAdminBillingDocumentController_updateOneById } from '@api-client/generated/AdminBillingDocumentController/updateOneById';
import { Schemas } from '@api-client/generated/types';
import { Loader } from '@components';
import { currencyCodes } from '@constants';
import { FilePreview } from '@entities';
import { useCurrentCompany, useCurrentCompanyId } from '@hooks';
import {
  getApiErrorMessage,
  isPreviewPending,
  PREVIEW_POLL_INTERVAL_MS,
  PreviewableFile,
} from '@utils';

import ModalBillingUploadDocument from '../../modals/ModalBillingUploadDocument';
import BillingTabDocumentEmpty from '../BillingTabDocumentEmpty';
import * as S from './styled';

type BillingTabDocumentProps = {
  periodId?: string;
  documentId: string | null;
  onAfterCreate?: (id: string) => void;
};

const { Title } = Typography;

const BillingTabDocument = (props: BillingTabDocumentProps) => {
  const companyId = useCurrentCompanyId();
  const { data: company } = useCurrentCompany();

  const [form] = Form.useForm();

  const [isModalUploadVisible, setIsModalUploadVisible] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    null
  );

  const { data: document, isPending: isLoading } =
    useAdminBillingDocumentController_findOneById({
      params: {
        companyId: companyId!,
        id: selectedDocumentId!,
      },
      config: {
        enabled: !!selectedDocumentId,
        // Poll while the HEIC→JPEG preview is still generating so it appears
        // without a manual reload; stop once it is ready/failed.
        refetchInterval: (query) =>
          isPreviewPending(query.state.data) ? PREVIEW_POLL_INTERVAL_MS : false,
      },
    });

  const { mutate: updateDocument } =
    useAdminBillingDocumentController_updateOneById();

  useEffect(() => {
    if (document) {
      const issueDate = document.documentMetadata?.issueDate;
      const dueDate = document.documentMetadata?.dueDate;

      form.setFieldsValue({
        isPaid: document.status === 'paid',
        documentMetadata: document.documentMetadata,
      });

      if (issueDate) {
        form.setFieldValue(['documentMetadata', 'issueDate'], dayjs(issueDate));
      }

      if (dueDate) {
        form.setFieldValue(['documentMetadata', 'dueDate'], dayjs(dueDate));
      }
    }
  }, [form, document]);

  useEffect(() => {
    setSelectedDocumentId(props.documentId);
  }, [props.documentId]);

  const handleAfterCreate = (id: string) => {
    setIsModalUploadVisible(false);
    setSelectedDocumentId(id);

    props.onAfterCreate?.(id);
  };

  const handleSubmit = (values: Schemas.AdminUpdateBillingDocumentDto) => {
    updateDocument(
      {
        parameter: {
          companyId: companyId!,
          id: selectedDocumentId!,
        },
        requestBody: values,
      },
      {
        onSuccess: () => {
          message.success('The document has been updated successfully');
          props.onAfterCreate?.(selectedDocumentId!);
        },
        onError: (error) => {
          message.error(getApiErrorMessage(error, 'Failed to update the document'));
        },
      }
    );
  };

  if (!selectedDocumentId && props.periodId) {
    return (
      <>
        <ModalBillingUploadDocument
          periodId={props.periodId}
          open={isModalUploadVisible}
          onCancel={() => setIsModalUploadVisible(false)}
          onAfterCreate={handleAfterCreate}
        />

        <BillingTabDocumentEmpty
          onUpload={() => setIsModalUploadVisible(true)}
        />
      </>
    );
  }
  const extension = document?.mimeType.split('/')[1];
  // Forward the HEIC→JPEG preview fields so FilePreview can resolve the preview.
  const { previewUrl, previewStatus } = (document ?? {}) as PreviewableFile;

  if (isLoading) {
    return <Loader />;
  }

  return (
    <S.Container gap={32}>
      {document && (
        <FilePreview
          width="440px"
          height="662px"
          file={
            {
              mimeType: document.mimeType,
              url: document.url,
              name: `${company?.name} - ${document.documentMetadata?.number}.${extension}`,
              previewUrl,
              previewStatus,
            } as unknown as Schemas.BillingDocument
          }
        />
      )}

      <Flex gap={16} vertical>
        <Flex align="center" justify="space-between">
          <Title level={5}>Document details</Title>
        </Flex>

        <Form
          form={form}
          onFinish={handleSubmit}
          layout="vertical"
          requiredMark={false}
        >
          {props.periodId && (
            <>
              <Form.Item label="Number" name={['documentMetadata', 'number']}>
                <Input size="large" />
              </Form.Item>

              <Row gutter={[12, 0]}>
                <Col span={12}>
                  <Form.Item
                    label="Issue date"
                    name={['documentMetadata', 'issueDate']}
                  >
                    <DatePicker
                      placeholder="DD.MM.YYYY"
                      format="DD.MM.YYYY"
                      size="large"
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label="Due date"
                    name={['documentMetadata', 'dueDate']}
                  >
                    <DatePicker
                      placeholder="DD.MM.YYYY"
                      format="DD.MM.YYYY"
                      size="large"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[12, 0]}>
                <Col span={12}>
                  <Form.Item
                    label="Amount"
                    name={['documentMetadata', 'amount']}
                  >
                    <InputNumber size="large" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label="Currency"
                    name={['documentMetadata', 'currencyCode']}
                  >
                    <Select
                      options={currencyCodes.map((code) => ({
                        label: code,
                        value: code,
                      }))}
                      size="large"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}
          <Form.Item
            label="Is paid "
            valuePropName="checked"
            layout="horizontal"
            colon={false}
            name="isPaid"
          >
            <Switch />
          </Form.Item>

          {document?.documentMetadata?.creditNoteReason && (
            <Form.Item>
              <p>
                <Typography.Text strong>Credit note reason:</Typography.Text>
              </p>
              <p>{document.documentMetadata.creditNoteReason}</p>
            </Form.Item>
          )}

          <Form.Item noStyle>
            <S.ButtonSubmit type="primary" htmlType="submit" size="large" block>
              Save
            </S.ButtonSubmit>
          </Form.Item>
        </Form>
      </Flex>
    </S.Container>
  );
};

export default BillingTabDocument;
